// React
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useState } from "react";
// Helpers
import {
  compact,
  get,
  indexOf,
  isEmpty,
  isFunction,
  map,
} from "@mefisto/utils";
// Framework
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { arrayMove } from "@dnd-kit/sortable";
import { Table as TableComponent } from "@material-ui/core";
import { classnames } from "ui/classnames";
import { makeStyles } from "ui/components";
import { InfiniteList } from "ui/list";
// Components
import TableContext from "../TableContext";
import TableHeader from "../TableHeader";
import { TableDragOverlay } from "ui/table";
import TableBody from "./components/TableBody";
import TableRowSkeleton from "./components/TableRowSkeleton";

////////////////////////////////////////////////////
/// Styles
////////////////////////////////////////////////////

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100%",
  },
  wrapper: {
    position: "relative",
    overflowX: "auto",
    height: "100%",
  },
  tableEmpty: {
    height: "100%",
  },
  tableWidth: {
    minWidth: theme.spacing(50),
    height: "100%",
  },
  loading: {
    minHeight: 200,
  },
  draggable: {
    paddingBottom: theme.spacing(1.5),
  },
  dragged: {
    userSelect: "none",
    touchAction: "none",
  },
}));

////////////////////////////////////////////////////
/// Component
////////////////////////////////////////////////////

const Table = ({
  data: v,
  hasMore,
  direction,
  orderBy,
  loading,
  error,
  columns,
  emptyTitle,
  emptySubtitle,
  disableHeader,
  draggable,
  skeletonX = 4,
  skeletonY = 20,
  children,
  onRefresh,
  onLoadMore,
  onSortChange,
}) => {
  // Styles
  const classes = useStyles();
  // State
  const [activeId, setActiveId] = useState();
  const [data, setData] = useState(v);
  // Draggable
  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );
  // Memo
  const rows = useMemo(() => {
    return compact(
      map(data, (data, index) => {
        if (children) {
          return isFunction(children) ? children({ data, index }) : children;
        }
      })
    );
  }, [data, children]);
  const items = useMemo(() => {
    return map(data, (data, index) => get(data, "key", index));
  }, [data]);
  const hasRows = useMemo(() => {
    return !isEmpty(rows);
  }, [rows]);
  const draggedRow = useMemo(() => {
    if (!activeId) {
      return null;
    }
    const indexOfRow = indexOf(items, activeId);
    if (indexOfRow === -1) {
      return null;
    }

    const row = rows[indexOfRow];
    return row;
  }, [activeId, rows, items]);
  // Handlers
  const handleDragStart = useCallback((event) => {
    console.log("🟡 DRAG START", event);
    setActiveId(event.active.id);
  }, []);
  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      console.log("⚪️ DRAG END", event);
      if (active.id !== over.id) {
        setData((data) => {
          const oldIndex = indexOf(items, active.id);
          const newIndex = indexOf(items, over.id);
          return arrayMove(data, oldIndex, newIndex);
        });
      }
      setActiveId(null);
    },
    [items]
  );
  const handleDragCancel = useCallback(() => {
    console.log("🔴 DRAG CANCEL");
    setActiveId(null);
  }, []);
  const handleSort = useCallback(
    (property) => {
      onSortChange &&
        onSortChange(
          property,
          orderBy === property && direction === "DESC" ? "ASC" : "DESC"
        );
    },
    [onSortChange, orderBy, direction]
  );
  const [saved, setSaved] = useState(false);
  // TODO: Find a better way to handle this
  // Effects
  useEffect(() => {
    if (v && !saved) {
      console.log("🟣 SET DATA!", v);
      setData(v);
      setSaved(true);
    }
  }, [v, children, data, saved]);
  // Render
  return (
    <TableContext data={data}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div
          className={classnames(classes.root, {
            [classes.dragged]: !!activeId,
            [classes.draggable]: draggable,
          })}
        >
          <div
            className={classnames(classes.wrapper, {
              [classes.loading]: loading || error,
            })}
          >
            {loading && <TableRowSkeleton x={skeletonX} y={skeletonY} />}
            <InfiniteList
              isEmpty={!hasRows}
              loadMore={onLoadMore}
              hasMore={loading || error ? false : hasMore}
            >
              <TableComponent
                stickyHeader
                className={classnames({
                  [classes.tableEmpty]: !hasRows,
                  [classes.tableWidth]: hasRows,
                })}
              >
                {!disableHeader && hasRows && (
                  <TableHeader
                    draggable={draggable}
                    columns={columns}
                    direction={direction}
                    orderBy={orderBy}
                    onSort={handleSort}
                  />
                )}
                <TableBody
                  draggable={draggable}
                  rows={rows}
                  items={items}
                  data={data}
                  loading={loading}
                  error={error}
                  emptyTitle={emptyTitle}
                  emptySubtitle={emptySubtitle}
                  onRefresh={onRefresh}
                />
              </TableComponent>
            </InfiniteList>
            <TableDragOverlay activeId={activeId} draggedRow={draggedRow} />
          </div>
        </div>
      </DndContext>
    </TableContext>
  );
};

Table.propTypes = {
  data: PropTypes.array,
  hasMore: PropTypes.bool,
  direction: PropTypes.string,
  orderBy: PropTypes.string,
  loading: PropTypes.bool,
  error: PropTypes.object,
  columns: PropTypes.array,
  emptyTitle: PropTypes.string,
  emptySubtitle: PropTypes.string,
  disableHeader: PropTypes.bool,
  skeletonX: PropTypes.number,
  skeletonY: PropTypes.number,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  onRefresh: PropTypes.func,
  onLoadMore: PropTypes.func,
  onSortChange: PropTypes.func,
};

export default Table;
